import parsePhoneNumberFromString, { CountryCode } from "libphonenumber-js";
import moment from "moment";
import { TagType } from "../components/cards/tagCard";
import { doNotification } from "../components/toast/nofitication";
import {
  CertificateStatus,
  ProfileType,
  SingleUser,
} from "../redux/features/auth/auth.type";
import { API_BASE_URL } from "../utils/constants";
import { OnboardingCompletion } from "../utils/types";

export const convertSize = (size: number) => {
  if (size > 1048576) {
    return `${Math.ceil(size / 1048576)} MB`;
  }
  return `${Math.ceil(size / 1024)} KB`;
};

export const getTextFromStatus = (status?: CertificateStatus) => {
  switch (status) {
    case "active":
      return "View document";
    case "pending":
      return "Pending review...";
    case "submitted":
      return "View document";
    case "approved":
      return "View document";
    case "rejected":
      return "Rejected";
    default:
      return "View document";
  }
};

export const decodeUrl = (url?: string) => {
  if (!url) return undefined;
  return url.includes("cloudinary") || url.startsWith("/assets/images")
    ? url
    : `${API_BASE_URL}${url.slice(7)}`;
};

export const getBase64 = async (file: File, callback = () => {}) => {
  const result_base64 = await new Promise((resolve) => {
    let fileReader = new FileReader();
    fileReader.onload = (e) => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });

  return (result_base64 as string)?.split(",")[1];
};

export const copyToClipBoard = (text: string) => {
  navigator?.clipboard?.writeText(text);
  doNotification("Text copied", "info");
};

/**
 * Checks if profile account passed has been approved for operation
 * Always defauts to true for non-cooperative profiles (i.e member profiles)
 */
export const isProfileApproved = (
  profile?: SingleUser & {
    type: ProfileType;
  },
) => {
  return profile?.type === "cooperative" ? profile?.isApproved : true;
};

export const base64ToBlob = (
  base64: string,
  type = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
) => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type });
};

export const handleError = (error: any, toastError = false) => {
  if (toastError) {
    const err =
      error?.error?.data?.error ??
      error?.error?.data?.detail ??
      error?.error?.data?.data ??
      "An error occurred";

    doNotification(String(err), "error");
  }
};

export const updateOnboardingCompletionStatus = (
  profile: SingleUser & { type: ProfileType },
): OnboardingCompletion => {
  const status = profile?.status;

  //   Initialize the response object
  const responseObject = {
    isAllComplete: false,
    isGetStartedComplete: false,
    isGetStartedStarted: false,
    isOfficialsComplete: false,
    isOfficialsStarted: false,
    isBankDetailsComplete: false,
    isMembershipFormComplete: false,
    onboardingCompletionProgress: 0,
    status: status,
  };

  // Onboarding completion status computation for member profiles
  if (profile?.type === "member") {
    const memberKYCDetails = [
      !!cleanHashes(profile?.FirstName),
      !!cleanHashes(profile?.LastName),
      // !!cleanHashes(profile?.MiddleName),
      !!cleanHashes(profile?.DateOfBirth),
      !!cleanHashes(profile?.PhoneNumber),
      !!cleanHashes(profile?.Email),
      !!cleanHashes(profile?.Address),
      !!cleanHashes(profile?.Country),
      !!cleanHashes(profile?.Picture),
      !!cleanHashes(profile?.State),
    ];

    responseObject.isGetStartedStarted = memberKYCDetails.some((i) => i);
    responseObject.isGetStartedComplete = memberKYCDetails.every((i) => i);
    responseObject.isBankDetailsComplete = (profile?.BankDetail ?? 0) > 0;

    const allStatuses = [...memberKYCDetails, profile?.BankDetail > 0];

    const completedCount = allStatuses.filter((el) => el).length;

    responseObject.onboardingCompletionProgress = Math.ceil(
      (completedCount / allStatuses?.length) * 100,
    );

    responseObject.isAllComplete = allStatuses?.every((el) => el);

    return responseObject;
  }

  // Onboarding completion status computation for cooperative profiles
  else {
    // If certificate is isRejected, update cetificate completion status to false
    if (profile?.Certificatestatus === "rejected") {
      status.certificate = false;
    }

    //   Check completion status of get started
    const cooperativeKYBDetails = [
      !!status?.description,
      !!status?.byelaw,
      !!status?.certificate,
      !!status?.logo,
    ];

    responseObject.isGetStartedComplete = cooperativeKYBDetails.every((i) => i);
    responseObject.isGetStartedStarted = cooperativeKYBDetails.some((i) => i);

    //   Check completion status of Inviting officials
    const officialsCompletionStatus =
      status?.official?.map((off) => {
        return off?.status;
      }) || [];

    responseObject.isOfficialsComplete = officialsCompletionStatus?.every(
      (el) => el,
    );
    responseObject.isOfficialsStarted = officialsCompletionStatus?.some(
      (i) => i,
    );
    responseObject.isBankDetailsComplete = !!status?.bankDetail;
    responseObject.isMembershipFormComplete = !!status?.membershipForm;

    const allStatuses = [
      ...cooperativeKYBDetails,
      !!status?.bankDetail,
      !!status?.membershipForm,
      ...officialsCompletionStatus,
    ];

    const completedCount = allStatuses.filter((el) => el).length;

    responseObject.onboardingCompletionProgress = Math.ceil(
      (completedCount / allStatuses?.length) * 100,
    );

    responseObject.isAllComplete = allStatuses?.every((el) => el);

    return responseObject;
  }
};

export const getDateDifference = (
  startDate: string,
  endDate: Date = new Date(),
) => {
  const sDate = new Date(startDate).getTime();
  const eDate = endDate.getTime();

  const dateDiff = eDate - sDate;

  if (dateDiff / 1_000 <= 60) {
    return `${formatToNumber(dateDiff / 1_000)} seconds`;
  } else if (dateDiff / 60_000 <= 60) {
    return `${formatToNumber(dateDiff / 60_000)} minutes`;
  } else if (dateDiff / 3_600_000 <= 24) {
    return `${formatToNumber(dateDiff / 3_600_000)} hours`;
  } else {
    return `${formatToNumber(dateDiff / 86_400_000)} days`;
  }
};

export const formatDate = (
  date: string | Date | moment.Moment | undefined | null,
  format: string = "MMMM DD, YYYY",
  ignoreISOTime: boolean = false,
): string => {
  try {
    if (!date) {
      return "";
    }
    let dateVal = date;

    if (typeof dateVal === "string" && ignoreISOTime) {
      dateVal = dateVal.split("T")[0];
    }

    const momentDate = moment(dateVal);

    if (!momentDate.isValid()) {
      throw new Error("Invalid date");
    }

    return momentDate.format(format);
  } catch (error) {
    return "Invalid date";
  }
};

export const cleanHashes = (val?: string) => {
  return val?.replaceAll("#", "");
};

export const parseDateStringToDate = (
  dateString?: string,
  format?: string,
  strict?: boolean,
): Date | null => {
  try {
    if (!dateString) {
      return null;
    }

    const momentDate = moment(dateString, format, strict);
    if (!momentDate.isValid()) {
      throw new Error("Invalid date format");
    }
    return momentDate.toDate();
  } catch (error) {
    return null;
  }
};

export const isPhoneNumberValid = (
  number: string,
  countryCode: CountryCode,
) => {
  const cleanedNumber = number.replaceAll(" ", "");

  const isValid = /^\+?\d+$/.test(cleanedNumber);

  if (!isValid) {
    return false;
  }

  const phonePrefixes = ["70", "71", "80", "81", "90", "91"];

  const parsedNumber = parsePhoneNumberFromString(cleanedNumber, countryCode);

  return countryCode === "NG"
    ? parsedNumber?.isValid() &&
        parsedNumber?.nationalNumber?.length === 10 &&
        phonePrefixes.includes(parsedNumber?.nationalNumber?.slice(0, 2))
    : (parsedNumber?.isValid() ?? false);
};

export const formatToCurrency = (
  amount: number | string,
  {
    decimalPlaces = 2,
    currency,
  }: {
    decimalPlaces?: number;
    currency?: string;
  },
) => {
  return formatToNumber(amount, {
    decimalPlaces: decimalPlaces,
    style: !!currency ? "currency" : "decimal",
    currency: currency,
  });
};

export const formatToNumber = (
  amount: number | string,
  {
    decimalPlaces = 2,
    style = "decimal",
    currency,
  }: {
    decimalPlaces?: number;
    style?: keyof Intl.NumberFormatOptionsStyleRegistry;
    currency?: string;
  } = {},
) => {
  if (amount === Infinity) {
    return "∞";
  }

  let amountStr = "";

  amountStr = `${String(amount).split(".")[0]}.${
    String(amount).split(".")[1] ?? "00"
  }`;

  amountStr = amountStr.replace(/[^\d.]/g, "");

  const numericAmount = parseFloat(amountStr);

  if (isNaN(numericAmount)) {
    return "";
  }

  let result = "";

  const formatter = new Intl.NumberFormat("en-NG", {
    style: style,
    currency: currency,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  result = formatter.format(
    style === "percent" ? numericAmount / 100 : numericAmount,
  );

  return result.replace(".00", "");
};

export const removeElementAtIndex = <T>(
  arrayOriginal: T[],
  index: number,
): T[] => {
  const array = JSON.parse(JSON.stringify(arrayOriginal));
  // Check if the index is within bounds
  if (index >= 0 && index < array.length) {
    array.splice(index, 1);
  }
  return array;
};

export const parseStringToNumber = (
  rawString?: string | number,
): number | null => {
  try {
    const numericString =
      String(rawString ?? "").replace(/[^\d.-]/g, "") !== ""
        ? String(rawString ?? "").replace(/[^\d.-]/g, "")
        : "0";

    const numericValue = parseFloat(numericString);

    if (isNaN(numericValue)) {
      throw new Error("Invalid naira format");
    }

    return numericValue;
  } catch (error) {
    return null;
  }
};

export const isPWA = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
};

export const deepCopy = <T extends object>(item: T): T => {
  return JSON.parse(JSON.stringify(item));
};

export const moveElement = <T>(
  array: T[],
  startIndex: number,
  endIndex: number,
): T[] => {
  const arrayCopy = JSON.parse(JSON.stringify(array));
  const [element] = arrayCopy.splice(startIndex, 1);
  arrayCopy.splice(endIndex, 0, element);
  return arrayCopy;
};

export const getPageFromOffset = (
  offset: number | string,
  limit: number | string,
) => {
  return Math.floor(Number(offset) / Number(limit)) + 1;
};

export const getOffsetFromPage = (
  page: number | string,
  limit: number | string,
) => {
  return (Number(page) - 1) * Number(limit);
};

export const getUploadStatus = (
  approved: boolean,
  approvalLevel: number,
  isOnline: boolean,
  type: "debit" | "credit",
): {
  text: string;
  type: TagType;
} => {
  if (approved && !isOnline && type === "credit") {
    return {
      text: "Approved",
      type: "success",
    };
  } else if (approved && (isOnline || type === "debit")) {
    return {
      text: "Successful",
      type: "success",
    };
  } else if (!approved && approvalLevel > 0) {
    return {
      text: "Pending Approval",
      type: "pending",
    };
  } else if (!approved && (approvalLevel === 0 || approvalLevel === null)) {
    return {
      text: "Rejected",
      type: "danger",
    };
  } else {
    return {
      text: "Unknown",
      type: "danger",
    };
  }
};

export const downloadExcel = (base64: string, fileName = "file") => {
  const blob = base64ToBlob(base64);
  const link = document.createElement("a");

  link.href = URL.createObjectURL(blob);
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getTimeAgo = (timeString: string): string => {
  const time = new Date(timeString).getTime();
  const now = Date.now();
  const secondsAgo = Math.floor((now - time) / 1000);

  if (secondsAgo < 60) {
    return `${secondsAgo} second${secondsAgo !== 1 ? "s" : ""} ago`;
  }

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) {
    return `${minutesAgo} minute${minutesAgo !== 1 ? "s" : ""} ago`;
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) {
    return `${hoursAgo} hour${hoursAgo !== 1 ? "s" : ""} ago`;
  }

  const daysAgo = Math.floor(hoursAgo / 24);
  if (daysAgo < 30) {
    return `${daysAgo} day${daysAgo !== 1 ? "s" : ""} ago`;
  }

  const monthsAgo = Math.floor(daysAgo / 30);
  if (monthsAgo < 12) {
    return `${monthsAgo} month${monthsAgo !== 1 ? "s" : ""} ago`;
  }

  const yearsAgo = Math.floor(monthsAgo / 12);
  return `${yearsAgo} year${yearsAgo !== 1 ? "s" : ""} ago`;
};

export const formatNumberShort = (
  value: number,
  digits: number = 1,
): string => {
  if (isNaN(value)) return "0";

  const units = [
    { value: 1_000_000_000_000, symbol: "T" },
    { value: 1_000_000_000, symbol: "B" },
    { value: 1_000_000, symbol: "M" },
    { value: 1_000, symbol: "K" },
  ];

  for (const unit of units) {
    if (Math.abs(value) >= unit.value) {
      return (
        (value / unit.value)
          .toFixed(digits)
          .replace(/\.0+$|(\.[0-9]*[1-9])0+$/, "$1") + unit.symbol
      );
    }
  }

  return value.toString();
};

export const maskEmail = (email: string) => {
  const [localPart, domain] = email.split("@");
  const maskedLocalPart = localPart.slice(0, 5) + "****";
  return `${maskedLocalPart}@${domain}`;
};

export const maskPhoneNumber = (phone: string) => {
  const maskedPhone = phone.slice(0, 3) + "***" + phone.slice(6);
  return maskedPhone;
};

export const getChannnelText = (isInApp?: boolean) => {
  return isInApp ? "In-App" : "Offline";
};

export const getCurrencySymbol = (
  currency: string,
  locale: string = "en-NG",
): string => {
  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).formatToParts(0);

  const symbol = parts.find((p) => p.type === "currency")?.value;

  if (!symbol) {
    throw new Error(`Invalid currency code: ${currency}`);
  }

  return symbol;
};

export const parsePhoneNumber = (number: string) => {
  const cleanedNumber = number.replaceAll(" ", "");

  const isValid = /^\+?\d+$/.test(cleanedNumber);

  if (!isValid) {
    return undefined;
  }

  return parsePhoneNumberFromString(cleanedNumber);
};

export const splitCamelCase = (word: string) => {
  return word.replaceAll(/([a-z])([A-Z])/g, "$1 $2");
};
